import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import axios from "axios";
import { BASE_API_URL } from "store/Me";
import Modal from "components/shared/Modal";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import type { ActivationCheckResponse } from "shared/types/ActivationCheckResponse";
import { SITES_QUERY_KEY } from "./siteQueryKeys";
import PhysicalKeySyncSuggestionDialog from "components/shared/PhysicalKeySyncSuggestionDialog";
import {
  type PhysicalKeySyncSuggestion,
  physicalKeySyncSuggestionsOf,
} from "components/shared/physicalKeySyncSuggestions";

// Lifecycle status of a Site. Mirrors ConnectApi's SiteStatus enum
// (src/Livly.Connect.Models/Enums/Enums.cs) -- not yet in the NSwag-generated client.
export type SiteStatus = "Staging" | "Active" | "Degraded" | "Archived";

export const SITE_STATUS_OPTIONS: SiteStatus[] = ["Staging", "Active", "Degraded", "Archived"];

async function getSiteActivationCheck(siteId: string): Promise<ActivationCheckResponse> {
  const result = await axios.get<ActivationCheckResponse>(
    `${BASE_API_URL}/api/Sites/${siteId}/status/activation-check`,
  );
  return result.data;
}

interface SiteStatusModalProps {
  show: boolean;
  onHide: () => void;
  siteId: string;
  currentStatus?: SiteStatus | null;
}

export default function SiteStatusModal({ show, onHide, siteId, currentStatus }: SiteStatusModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedStatus, setSelectedStatus] = useState<SiteStatus>("Staging");
  const [syncSuggestions, setSyncSuggestions] = useState<PhysicalKeySyncSuggestion[]>([]);

  useEffect(() => {
    if (show) {
      setSelectedStatus(currentStatus ?? "Staging");
    }
  }, [show, currentStatus]);

  const isActivating = selectedStatus === "Active" && currentStatus !== "Active";

  const {
    data: activationCheck,
    isError: activationCheckError,
    isLoading: activationCheckLoading,
  } = useQuery({
    queryKey: ["site-activation-check", siteId],
    queryFn: () => getSiteActivationCheck(siteId),
    enabled: show && isActivating && !!siteId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: SiteStatus) =>
      axios.patch(`${BASE_API_URL}/api/Sites/${siteId}/status`, { status }),
  });

  const handleClose = () => {
    onHide();
  };

  const handleConfirm = () => {
    updateStatusMutation.mutate(selectedStatus, {
      onSuccess: (response) => {
        // The SiteResponse may suggest Fobs syncs; shown after this modal closes.
        setSyncSuggestions(physicalKeySyncSuggestionsOf(response?.data));
        enqueueSnackbar(`Site status updated to ${selectedStatus}`, { variant: "success" });
        queryClient.invalidateQueries({ queryKey: ["sites", "details", siteId] });
        queryClient.invalidateQueries({ queryKey: [SITES_QUERY_KEY] });
        handleClose();
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error?.message || "Failed to update site status";
        enqueueSnackbar(message, { variant: "error" });
      },
    });
  };

  return (
    <>
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Update Site Status</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 20 }}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Current status: <strong>{currentStatus ?? "Staging"}</strong>
          </p>
          <Select
            value={selectedStatus}
            onValueChange={(status) => setSelectedStatus(status as SiteStatus)}
          >
            <SelectTrigger aria-label="Site status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SITE_STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isActivating && (
            activationCheckLoading ? (
              <div className="flex items-center gap-1">
                <Spinner />
                <span className="text-sm">Checking existing guest access...</span>
              </div>
            ) : activationCheckError ? (
              <Alert variant="warning">
                Unable to verify existing guest access. You may still proceed,
                but verify access after activation.
              </Alert>
            ) : activationCheck?.recommendation === "reonboardFirst" ? (
              <Alert variant="warning">
                {activationCheck.guestCount} guest(s) currently have resolvable access into this site's
                locks. Recommend running Re-onboard on the site's property before activating. You may
                still proceed without re-onboarding.
              </Alert>
            ) : (
              <Alert variant="info">
                No guests currently have resolvable access into this site. Proceeding will activate it
                with no existing residents provisioned.
              </Alert>
            )
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={updateStatusMutation.isPending || selectedStatus === (currentStatus ?? "Staging")}
        >
          {updateStatusMutation.isPending ? <Spinner className="size-5" /> : "Update Status"}
        </Button>
      </Modal.Footer>
    </Modal>
    <PhysicalKeySyncSuggestionDialog
      suggestions={syncSuggestions}
      onClose={() => setSyncSuggestions([])}
    />
    </>
  );
}
